import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode } from "vue"

import { computed } from 'vue';
import type { Address } from '@src/view_models/Address';
import type { Country } from '@src/view_models/Country';
import type { Salutation } from '@src/view_models/Salutation';
import { clearStreetAndBuildingNumberSeparator } from '@src/util/street_and_building_number_tools';
import { useI18n } from 'vue-i18n';

interface Props {
	address: Address;
	countries: Array<Country>;
	salutations: Array<Salutation>;
	receiptNeeded: boolean;
}

export default /*@__PURE__*/_defineComponent({
  __name: 'AddressSummary',
  props: {
    address: {},
    countries: {},
    salutations: {},
    receiptNeeded: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;
const { t } = useI18n();

const companyName = computed<string>( () => {
	if ( [ 'firma', 'company_with_contact' ].includes( props.address.addressType ) ) {
		return props.address.companyName || '';
	}
	return '';
} );

const name = computed( () => {
	if ( [ 'person', 'email', 'company_with_contact' ].includes( props.address.addressType ) ) {

		if ( !props.address.firstName?.trim() || !props.address.lastName?.trim() ) {
			return '';
		}

		const nameParts: string[] = [];

		const salutationObject = props.salutations.find( s => s.value === props.address.salutation );
		const salutation = salutationObject?.display?.trim() || '';
		const title = props.address.title?.trim() || '';

		if ( title || salutation ) {
			const key = title
				? 'address_salutation_academic_title'
				: 'address_salutation_no_academic_title';

			const renderedSalutationOrTitle = t( key, { salutation, title } );
			if ( renderedSalutationOrTitle.trim() ) {
				nameParts.push( renderedSalutationOrTitle.trim() );
			}
		}

		nameParts.push( props.address.firstName, props.address.lastName );

		return nameParts.join( ' ' );
	}
	return '';
} );

const cleanedStreetAddress = computed( () => {
	if ( !props.receiptNeeded ) {
		return '';
	}
	return clearStreetAndBuildingNumberSeparator( props.address.street || '' );
} );

const postcodeCity = computed( () => {
	if ( !props.receiptNeeded ) {
		return '';
	}
	return props.address.postcode && props.address.city
		? `${ props.address.postcode } ${ props.address.city }`
		: '';
}
);

const country = computed( () => {
	if ( !props.receiptNeeded ) {
		return '';
	}
	const countryObject = props.countries.find(
		( c ) =>
			c.countryCode === props.address.country
	);
	return countryObject ? countryObject.countryFullName : '';
} );

const showCountry = computed( () => {
	if ( !props.receiptNeeded ) {
		return '';
	}
	return Boolean( postcodeCity.value && country.value );
} );

const email = computed( () => props.address.email || '' );

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("h3", null, _toDisplayString(_ctx.$t('form_summary_contact_details_header')), 1),
    _createElementVNode("p", null, [
      (name.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("strong", null, _toDisplayString(name.value), 1),
            _cache[0] || (_cache[0] = _createElementVNode("br", null, null, -1))
          ], 64))
        : _createCommentVNode("", true),
      (companyName.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createElementVNode("strong", null, _toDisplayString(companyName.value), 1),
            _cache[1] || (_cache[1] = _createElementVNode("br", null, null, -1))
          ], 64))
        : _createCommentVNode("", true),
      (cleanedStreetAddress.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
            _createTextVNode(_toDisplayString(cleanedStreetAddress.value), 1),
            _cache[2] || (_cache[2] = _createElementVNode("br", null, null, -1))
          ], 64))
        : _createCommentVNode("", true),
      (postcodeCity.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
            _createTextVNode(_toDisplayString(postcodeCity.value), 1),
            _cache[3] || (_cache[3] = _createElementVNode("br", null, null, -1))
          ], 64))
        : _createCommentVNode("", true),
      (showCountry.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
            _createTextVNode(_toDisplayString(country.value), 1),
            _cache[4] || (_cache[4] = _createElementVNode("br", null, null, -1))
          ], 64))
        : _createCommentVNode("", true),
      (email.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 5 }, [
            _createTextVNode(_toDisplayString(email.value), 1),
            _cache[5] || (_cache[5] = _createElementVNode("br", null, null, -1))
          ], 64))
        : _createCommentVNode("", true)
    ])
  ], 64))
}
}

})